import React, { useMemo, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, TextField, Box, Typography,
    IconButton, CircularProgress, InputAdornment, Alert, LinearProgress, Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility, VisibilityOff,
    Lock as LockIcon,
    LockReset as LockResetIcon,
    Person as PersonIcon,
    LockOutlined as LockOutlinedIcon,
    CheckCircle as CheckIcon,
    Cancel as CrossIcon,
    ShieldOutlined as ShieldIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { BRAND, FONT_STACK, GRADIENT_NAVY } from '../brand';
import { change_password, changestaffpassword } from './BaseURL';

// ============================================================================
//  ChangePasswordDialog — a signed-in user changing their own password
// ============================================================================
//
//  Same policy and same look as the forced first-login prompt, so the two
//  never disagree about what a good password is. The difference is that the
//  current password is required here, and this dialog can be dismissed.
//
//  It posts to /account/change-password, NOT the legacy /changestaffpassword.
//  The old call sent only `email` while the server verified against
//  `trans_code`, so the lookup matched nothing and every attempt returned
//  "Current password is incorrect" — the feature could not succeed at all.

// Mirrors validatePassword() in backend/setup_routes.js.
const RULES = [
    { key: 'len',   label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { key: 'lower', label: 'A lowercase letter',    test: (p) => /[a-z]/.test(p) },
    { key: 'upper', label: 'An uppercase letter',   test: (p) => /[A-Z]/.test(p) },
    { key: 'digit', label: 'A number',              test: (p) => /[0-9]/.test(p) },
    { key: 'sym',   label: 'A symbol (! @ # $)',    test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function score(password, username) {
    if (!password) return { pct: 0, label: '', color: BRAND.line };
    let passed = RULES.filter((r) => r.test(password)).length;
    if (password.length >= 12) passed += 1;
    if (username && password.toLowerCase().includes(String(username).toLowerCase())) passed -= 2;

    const pct = Math.max(0, Math.min(100, (passed / 6) * 100));
    if (pct < 40)  return { pct, label: 'Weak',        color: BRAND.danger };
    if (pct < 70)  return { pct, label: 'Fair',        color: BRAND.warning };
    if (pct < 100) return { pct, label: 'Strong',      color: BRAND.green };
    return             { pct, label: 'Very strong',    color: BRAND.greenDark };
}

function PasswordField({ label, value, onChange, show, onToggle, error, helperText, autoFocus }) {
    return (
        <TextField
            fullWidth
            label={label}
            autoFocus={autoFocus}
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={helperText || ' '}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '11px', background: '#fff',
                    '& fieldset':             { borderColor: BRAND.line },
                    '&:hover fieldset':       { borderColor: BRAND.navy },
                    '&.Mui-focused fieldset': { borderColor: BRAND.navy, borderWidth: '1.5px' },
                    '&.Mui-focused':          { boxShadow: `0 0 0 4px ${BRAND.navy}14` },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: BRAND.navy, fontWeight: 600 },
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <LockIcon sx={{ color: BRAND.navy, fontSize: 19 }} />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={onToggle} edge="end" size="small"
                            aria-label={show ? 'Hide password' : 'Show password'}>
                            {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}

function ChangePasswordDialog({ open, userInfo, onClose }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword]         = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError]                     = useState('');
    const [loading, setLoading]                 = useState(false);
    const [showCurrent, setShowCurrent]         = useState(false);
    const [showNew, setShowNew]                 = useState(false);
    const [showConfirm, setShowConfirm]         = useState(false);

    const username = userInfo?.username || '';
    const strength = useMemo(() => score(newPassword, username), [newPassword, username]);

    const allRulesPass = RULES.every((r) => r.test(newPassword));
    const matches      = newPassword.length > 0 && newPassword === confirmPassword;
    const canSubmit    = !!currentPassword && allRulesPass && matches && !loading;

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
    };

    const handleClose = () => {
        if (loading) return;
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        setError('');

        if (!currentPassword) { setError('Enter your current password.'); return; }

        const failed = RULES.find((r) => !r.test(newPassword));
        if (failed) { setError(`Your new password needs: ${failed.label.toLowerCase()}.`); return; }

        if (username && newPassword.toLowerCase().includes(username.toLowerCase())) {
            setError('Your password must not contain your username.');
            return;
        }
        if (newPassword !== confirmPassword) { setError('The two new passwords do not match.'); return; }
        if (currentPassword === newPassword) {
            setError('Your new password must be different from your current one.');
            return;
        }

        // trans_code is what the server verifies against — the old dialog
        // omitted it, which is why this never worked.
        const transCode = userInfo?.trans_code;
        if (!transCode) {
            setError('Your session is incomplete. Please sign out and sign in again.');
            return;
        }

        try {
            setLoading(true);
            const { data } = await axios.post(change_password, {
                trans_code: transCode,
                currentPassword,
                newPassword,
            });

            if (data.success) {
                resetForm();
                onClose();
                Swal.fire({
                    title: 'Password changed',
                    text: 'Your new password takes effect the next time you sign in.',
                    icon: 'success',
                    confirmButtonColor: BRAND.green,
                });
            } else {
                setError(data.message || 'Could not update your password.');
            }
        } catch (err) {
            // Older backends have not been restarted with the new route yet.
            if (err.response?.status === 404) {
                try {
                    const { data } = await axios.post(changestaffpassword, {
                        email: userInfo?.email,
                        trans_code: transCode,           // ← the piece that was missing
                        current_password: currentPassword,
                        new_password: newPassword,
                    });
                    if (data.success) {
                        resetForm();
                        onClose();
                        Swal.fire({
                            title: 'Password changed',
                            text: 'Your new password takes effect the next time you sign in.',
                            icon: 'success',
                            confirmButtonColor: BRAND.green,
                        });
                        return;
                    }
                    setError(data.message || 'Could not update your password.');
                } catch (legacyErr) {
                    setError(legacyErr.response?.data?.message || 'Could not update your password.');
                }
            } else {
                setError(err.response?.data?.message || 'Could not update your password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', fontFamily: FONT_STACK, m: { xs: 1.5, sm: 4 } } }}
        >
            {/* ── header ── */}
            <Box sx={{ background: GRADIENT_NAVY, px: { xs: 2.5, sm: 3.5 }, py: 2.75, position: 'relative', overflow: 'hidden' }}>
                <Box aria-hidden sx={{ position: 'absolute', top: -60, right: -50, width: 190, height: 190, borderRadius: '50%', background: `radial-gradient(circle, ${BRAND.green}40 0%, transparent 65%)` }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <LockResetIcon sx={{ fontSize: 23, color: '#fff' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '1.3px', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>
                            Account security
                        </Typography>
                        <Typography sx={{ fontSize: 18.5, fontWeight: 800, color: '#fff', letterSpacing: '-.3px', lineHeight: 1.25 }}>
                            Change your password
                        </Typography>
                    </Box>
                    <IconButton
                        size="small" onClick={handleClose} disabled={loading}
                        sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,.26)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, background: '#fff' }}>

                {/* who this is for — the username is fixed, and saying so here
                    heads off "can I change my username too?" */}
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.75, mb: 3,
                        px: 2, py: 1.5, borderRadius: '12px',
                        background: BRAND.navySoft, border: `1px solid ${BRAND.navy}1F`,
                    }}
                >
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0, border: `1px solid ${BRAND.navy}22` }}>
                        <PersonIcon sx={{ fontSize: 19, color: BRAND.navy }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                            Signed in as
                        </Typography>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: BRAND.navy, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.4 }} noWrap>
                            {username || userInfo?.email || '—'}
                        </Typography>
                    </Box>
                    <Tooltip title="Your username is permanent and cannot be changed" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, px: 1.125, py: .375, borderRadius: '20px', background: '#fff', border: `1px solid ${BRAND.line}`, flexShrink: 0 }}>
                            <LockOutlinedIcon sx={{ fontSize: 12, color: BRAND.inkMuted }} />
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: BRAND.inkMuted }}>Fixed</Typography>
                        </Box>
                    </Tooltip>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '11px' }}>{error}</Alert>}

                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint, mb: 1.5 }}>
                    Confirm it's you
                </Typography>
                <PasswordField
                    label="Current password" autoFocus
                    value={currentPassword} onChange={(v) => { setCurrentPassword(v); setError(''); }}
                    show={showCurrent} onToggle={() => setShowCurrent((v) => !v)}
                />

                <Box sx={{ height: 1, background: BRAND.line, my: 2 }} />

                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint, mb: 1.5 }}>
                    Choose a new password
                </Typography>
                <PasswordField
                    label="New password"
                    value={newPassword} onChange={(v) => { setNewPassword(v); setError(''); }}
                    show={showNew} onToggle={() => setShowNew((v) => !v)}
                />
                <PasswordField
                    label="Confirm new password"
                    value={confirmPassword} onChange={(v) => { setConfirmPassword(v); setError(''); }}
                    show={showConfirm} onToggle={() => setShowConfirm((v) => !v)}
                    error={!!confirmPassword && confirmPassword !== newPassword}
                    helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ' '}
                />

                {/* strength + rules */}
                <Box sx={{ p: 2.25, borderRadius: '12px', background: BRAND.canvas, border: `1px solid ${BRAND.line}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <ShieldIcon sx={{ fontSize: 15, color: BRAND.inkMuted }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: BRAND.inkMuted, letterSpacing: '.6px' }}>
                                PASSWORD STRENGTH
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: strength.color }}>
                            {strength.label}
                        </Typography>
                    </Box>

                    <LinearProgress
                        variant="determinate" value={strength.pct}
                        sx={{
                            height: 6, borderRadius: 3, mb: 2, backgroundColor: BRAND.line,
                            '& .MuiLinearProgress-bar': { backgroundColor: strength.color, borderRadius: 3 },
                        }}
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                        {RULES.map((rule) => {
                            const ok = rule.test(newPassword);
                            return (
                                <Box key={rule.key} sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                                    {ok
                                        ? <CheckIcon sx={{ fontSize: 15, color: BRAND.green, flexShrink: 0 }} />
                                        : <CrossIcon sx={{ fontSize: 15, color: BRAND.inkFaint, flexShrink: 0 }} />}
                                    <Typography sx={{ fontSize: 12.5, color: ok ? BRAND.ink : BRAND.inkMuted, fontWeight: ok ? 600 : 400 }}>
                                        {rule.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, borderTop: `1px solid ${BRAND.line}`, background: '#fff', gap: 1.5 }}>
                <Button
                    onClick={handleClose} disabled={loading}
                    sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.inkMuted }}
                >
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    onClick={handleSubmit} variant="contained" disabled={!canSubmit}
                    startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <LockResetIcon />}
                    sx={{
                        px: 3.5, py: 1.05, borderRadius: '11px', textTransform: 'none', fontWeight: 700,
                        background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 55%, ${BRAND.greenDark} 100%)`,
                        boxShadow: `0 4px 14px ${BRAND.green}42`,
                        '&:hover':    { filter: 'brightness(1.06)' },
                        '&:disabled': { background: '#C7D2E0', color: '#fff', boxShadow: 'none' },
                    }}
                >
                    {loading ? 'Saving…' : 'Update password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChangePasswordDialog;
